import { useEffect } from 'react';

interface Options {
  /** Minimaler horizontaler Weg in Pixeln, um als Swipe zu zählen. */
  threshold?: number;
  /** Maximaler vertikaler Weg, sonst gilt die Geste als Scroll, nicht als Swipe. */
  verticalTolerance?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

const DEFAULT_THRESHOLD = 60;
const DEFAULT_VERTICAL_TOLERANCE = 60;

/**
 * Fügt globale Touch-Listener für horizontale Swipes hinzu. Ignoriert
 * Touches innerhalb von Form-Elementen (textarea/input/select/button)
 * und Elementen mit `data-no-swipe`, damit normale Bedienung der Inputs
 * und ausklappbarer Listenzeilen nicht betroffen ist.
 */
export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = DEFAULT_THRESHOLD,
  verticalTolerance = DEFAULT_VERTICAL_TOLERANCE,
}: Options): void {
  useEffect(() => {
    if (!onSwipeLeft && !onSwipeRight) return undefined;

    let startX = 0;
    let startY = 0;
    let active = false;

    const isInteractive = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      if (target.closest('input, textarea, select, button, a, [data-no-swipe]')) return true;
      return false;
    };

    const onStart = (e: TouchEvent): void => {
      if (e.touches.length !== 1) {
        active = false;
        return;
      }
      if (isInteractive(e.target)) {
        active = false;
        return;
      }
      const t = e.touches[0]!;
      startX = t.clientX;
      startY = t.clientY;
      active = true;
    };

    const onEnd = (e: TouchEvent): void => {
      if (!active) return;
      active = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dy) > verticalTolerance) return;
      if (dx <= -threshold && onSwipeLeft) onSwipeLeft();
      else if (dx >= threshold && onSwipeRight) onSwipeRight();
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [onSwipeLeft, onSwipeRight, threshold, verticalTolerance]);
}
